<template>
  <div class="modal-overlay" @click.self="close()">
    <div class="modal-card">

      <!-- Header -->
      <div class="modal-head">
        <div class="modal-title">
          <div class="title-icon">
            <i :class="is_view ? 'fa fa-eye' : is_edit ? 'fa fa-pencil-alt' : 'fa fa-desktop'"></i>
          </div>
          {{ is_edit && !is_view ? 'Update Workstation' : is_view ? 'View Workstation' : 'Create Workstation' }}
        </div>
        <button class="btn-close" @click="close()"><i class="fa fa-times"></i></button>
      </div>

      <!-- Body -->
      <div class="modal-body">

        <!-- Service Provider (admin) -->
        <div class="field-group" v-if="currentUser.u.is_admin">
          <label class="field-label">Service Provider <span class="req">*</span></label>
          <multiselect :disabled="is_edit" :options="getClients ? getClients : []" placeholder="Select service provider"
            label="name" track-by="id" :preselect-first="false" v-model="service_provider" class="ms-field">
          </multiselect>
          <span class="field-error" v-if="errors['client_id']">{{ errors['client_id'][0] }}</span>
        </div>

        <!-- Location -->
        <div class="field-group">
          <label class="field-label">Location <span class="req">*</span></label>
          <multiselect :disabled="is_edit" :options="getBLocations.data ? getBLocations.data : []"
            placeholder="Select location" label="name" track-by="id" :preselect-first="false" v-model="selected"
            @select="changeForm" class="ms-field"></multiselect>
          <span class="field-error" v-if="errors['client_id']">{{ errors['client_id'][0] }}</span>
        </div>

        <!-- Label + IP -->
        <div class="field-row">
          <div class="field-group">
            <label class="field-label">Workstation Label <span class="req">*</span></label>
            <input class="field-input" :class="{ 'has-error': errors['label'] }" type="text" :disabled="is_disabled"
              v-model="apikey.label" placeholder="e.g. Front Desk" />
            <span class="field-error" v-if="errors['label']">{{ errors['label'][0] }}</span>
          </div>
          <div class="field-group">
            <label class="field-label">Allowed IPs (CIDR) <span class="req">*</span></label>
            <input class="field-input" :class="{ 'has-error': errors['ip_address'] }" type="text" :disabled="is_edit"
              v-model="apikey.ip_address" placeholder="e.g. 192.168.1.0/24" />
            <span class="field-error" v-if="errors['ip_address']">{{ errors['ip_address'][0] }}</span>
          </div>
        </div>

        <!-- Active toggle -->
        <div class="toggle-row">
          <label class="toggle-item">
            <span class="toggle-wrap">
              <input type="checkbox" :disabled="is_disabled" v-model="apikey.is_active" class="toggle-input" />
              <span class="toggle-slider"></span>
            </span>
            <span class="toggle-label">Active</span>
          </label>
        </div>

        <!-- API Key (view only) -->
        <div class="field-group" v-if="is_view">
          <label class="field-label">API Token</label>
          <div class="token-display">
            <span ref="text" class="token-value">{{ apikey.api_key }}</span>
            <button class="btn-copy-token" @click="copy()" :title="copied ? 'Copied!' : 'Copy to clipboard'">
              <i :class="copied ? 'fa fa-check' : 'fa fa-copy'"></i>
              {{ copied ? 'Copied' : 'Copy' }}
            </button>
          </div>
        </div>

        <!-- DO Submission Config -->
        <template v-if="showDoFrom">
          <div class="section-divider">
            <span><i class="fa fa-cogs"></i> Dept. Order Submission Config</span>
          </div>

          <div class="field-group">
            <label class="field-label">Channeler <span class="req">*</span></label>
            <select class="field-select" v-model="apikey.do_channeler" :disabled="is_disabled">
              <option value="1">TRP</option>
              <option value="2">VETCONNEX</option>
            </select>
            <span class="field-error" v-if="errors['do_channeler']">{{ errors['do_channeler'][0] }}</span>
          </div>

          <template v-if="apikey.do_channeler != '2'">
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">LSID <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['do_lsid'] }" type="text"
                  :disabled="is_disabled" v-model="apikey.do_lsid" placeholder="LSID" />
                <span class="field-error" v-if="errors['do_lsid']">{{ errors['do_lsid'][0] }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Username <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['do_username'] }" type="text"
                  :disabled="is_disabled" v-model="apikey.do_username" placeholder="Username" />
                <span class="field-error" v-if="errors['do_username']">{{ errors['do_username'][0] }}</span>
              </div>
            </div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">Host <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['do_host'] }" type="text"
                  :disabled="is_disabled" v-model="apikey.do_host" placeholder="hostname or IP" />
                <span class="field-error" v-if="errors['do_host']">{{ errors['do_host'][0] }}</span>
              </div>
              <div class="field-group" style="flex:0 0 100px;">
                <label class="field-label">Port <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['do_port'] }" type="number"
                  :disabled="is_disabled" v-model="apikey.do_port" placeholder="8080" />
                <span class="field-error" v-if="errors['do_port']">{{ errors['do_port'][0] }}</span>
              </div>
            </div>
            <div class="field-row">
              <div class="field-group">
                <label class="field-label">NIST File Path <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['do_nist_filepath'] }" type="text"
                  :disabled="is_disabled" v-model="apikey.do_nist_filepath" placeholder="/path/to/nist" />
                <span class="field-error" v-if="errors['do_nist_filepath']">{{ errors['do_nist_filepath'][0] }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Sig File Path <span class="req">*</span></label>
                <input class="field-input" :class="{ 'has-error': errors['do_sig_filepath'] }" type="text"
                  :disabled="is_disabled" v-model="apikey.do_sig_filepath" placeholder="/path/to/sig" />
                <span class="field-error" v-if="errors['do_sig_filepath']">{{ errors['do_sig_filepath'][0] }}</span>
              </div>
            </div>
            <div class="field-group">
              <label class="field-label">Key File <span class="req">*</span></label>
              <div class="file-row">
                <span class="file-current" v-if="apikey.do_key_filepath">
                  <i class="fa fa-file-archive"></i> {{ apikey.do_key_filepath }}
                </span>
                <label class="btn-file" v-if="!is_disabled">
                  <input type="file" id="file" ref="file" :disabled="is_disabled" @change="handleFileUpload($event)" />
                  <i class="fa fa-upload"></i> {{ apikey.do_key_filepath ? 'Replace' : 'Upload' }}
                </label>
              </div>
              <span class="field-error" v-if="errors['do_key_filepath']">{{ errors['do_key_filepath'][0] }}</span>
            </div>
          </template>
        </template>

        <!-- Livescan Devices (edit mode only) -->
        <template v-if="is_edit">
          <div class="section-divider">
            <span><i class="fa fa-fingerprint"></i> Livescan Devices</span>
          </div>

          <!-- Add row -->
          <div class="device-add-row" v-if="!is_disabled">
            <input class="field-input" type="text" v-model="newSerial" placeholder="Enter serial number"
              @keyup.enter="addDevice()" />
            <button class="btn-device-add" @click="addDevice()" :disabled="!newSerial.trim()">
              <i class="fa fa-plus"></i> Add
            </button>
          </div>

          <!-- Device list -->
          <div class="device-list" v-if="devices.length">
            <div class="device-row" v-for="dev in devices" :key="dev.id">
              <!-- Inline edit -->
              <template v-if="editDevice && editDevice.id === dev.id">
                <input class="field-input device-edit-input" type="text" v-model="editDevice.serial_number"
                  @keyup.enter="saveEdit()" @keyup.escape="cancelEdit()" />
                <button class="btn-dev btn-dev-save" @click="saveEdit()" title="Save">
                  <i class="fa fa-check"></i>
                </button>
                <button class="btn-dev btn-dev-cancel" @click="cancelEdit()" title="Cancel">
                  <i class="fa fa-times"></i>
                </button>
              </template>
              <!-- Delete confirm -->
              <template v-else-if="deletingDeviceId === dev.id">
                <span class="device-confirm-text">Delete <strong>{{ dev.serial_number }}</strong>?</span>
                <button class="btn-dev btn-dev-del" @click="confirmDeleteDevice(dev.id)">
                  <i class="fa fa-trash"></i> Yes
                </button>
                <button class="btn-dev btn-dev-cancel" @click="deletingDeviceId = null">
                  <i class="fa fa-times"></i>
                </button>
              </template>
              <!-- Normal -->
              <template v-else>
                <span class="device-serial">{{ dev.serial_number }}</span>
                <div class="device-actions" v-if="!is_disabled">
                  <button class="btn-dev btn-dev-edit" @click="startEdit(dev)" title="Edit">
                    <i class="fa fa-pencil-alt"></i>
                  </button>
                  <button class="btn-dev btn-dev-del" @click="deletingDeviceId = dev.id" title="Delete">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </template>
            </div>
          </div>

          <!-- Empty state -->
          <div class="device-empty" v-else>
            <i class="fa fa-fingerprint"></i> No Livescan devices linked yet
          </div>
        </template>

      </div>

      <!-- Footer -->
      <div class="modal-foot" v-if="!is_disabled">
        <button class="btn-cancel" @click="close()">
          <i class="fa fa-times"></i> Cancel
        </button>
        <button class="btn-save" @click="save()">
          <i class="fa fa-check"></i> {{ is_edit ? 'Update' : 'Save' }}
        </button>
      </div>
      <div class="modal-foot" v-else>
        <button class="btn-cancel" style="flex:0 0 auto; padding:0 24px;" @click="close()">
          <i class="fa fa-times"></i> Close
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Multiselect from "vue-multiselect";
import {
  CREATE_APIKEY,
  FETCH_B_LOCATIONS,
  GENERATE_APIKEY,
  GET_APIKEY,
  GET_API_KEY,
  CREATE_API_KEY_DEVICES,
  DELETE_APIKEY_DEVICE,
  FETCH_CLIENTS
} from "@/core/services/store/actions.type";

export default {
  props: {
    showModal: { type: Boolean, default: false },
    is_edit: { type: Boolean, default: false },
    is_disabled: { type: Boolean, default: false },
    is_view: { type: Boolean, default: false },
    apikeyid: { type: Number, default: 0 },
    apikey: {
      type: Object,
      default: () => ({
        id: 0, label: "", api_key: "", client_id: "", ip_address: "",
        is_active: 0, do_lsid: "", do_username: "", do_host: "",
        do_nist_filepath: "", do_sig_filepath: "", do_port: "",
        do_key_filepath: "", do_channeler: "1", service_provider: "",
      })
    }
  },
  data() {
    return {
      errors: [],
      selected: "",
      key: 0,
      file: null,
      showDoFrom: false,
      service_provider: "",
      copied: false,
      devices: [],
      newSerial: "",
      editDevice: null,
      deletingDeviceId: null,
    };
  },
  components: { Multiselect },
  computed: {
    ...mapGetters(["getBLocations", "currentUser", "getClients", "getSpClients"])
  },
  created() {
    if (!this.currentUser.u.is_admin) {
      this.getLocationData();
    } else {
      this.$store.dispatch(FETCH_CLIENTS);
    }
    if (this.is_edit) {
      this.getApikey();
    } else {
      this.selected = "";
    }
  },
  mounted() {
    document.body.addEventListener("keyup", e => {
      if (e.key === "Escape") this.$emit("closemodal");
    });
  },
  methods: {
    handleFileUpload(event) {
      this.file = event.target.files[0];
    },
    copy() {
      navigator.clipboard.writeText(this.apikey.api_key).then(() => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 1800);
        this.$toastr.s("API key copied to clipboard");
      });
    },
    close() {
      this.$emit("closemodal");
    },
    async getApikey() {
      let res = await this.$store.dispatch(GET_APIKEY, this.apikeyid);
      this.selected = res[0].client;
      this.service_provider = res[0].service_provider;
      this.apikey.label = res[0].label;
      this.apikey.api_key = res[0].api_key;
      this.apikey.ip_address = res[0].ip_address;
      this.apikey.is_active = res[0].is_active;
      this.apikey.do_lsid = res[0].do_lsid;
      this.apikey.id = this.apikeyid;
      this.apikey.do_username = res[0].do_username;
      this.apikey.do_host = res[0].do_host;
      this.apikey.do_port = res[0].do_port;
      this.apikey.do_nist_filepath = res[0].do_nist_filepath;
      this.apikey.do_sig_filepath = res[0].do_sig_filepath;
      this.apikey.do_key_filepath = res[0].do_key_filepath;
      this.apikey.do_channeler = res[0].do_channeler;
      this.showDoFrom = !!this.selected?.show_apikey_deporderform;
      await this.loadDevices();
    },
    async genApiToken() {
      let res = await this.$store.dispatch(GENERATE_APIKEY);
      this.apikey.api_key = res.data;
    },
    changeForm(option) {
      this.showDoFrom = !!option.show_apikey_deporderform;
    },
    getLocationData() {
      this.$store.dispatch(FETCH_B_LOCATIONS, {});
      this.key += 1;
    },
    getLocationDataAdmin() {
      this.selected = "";
      this.$store.dispatch(FETCH_B_LOCATIONS, { service_provider: this.service_provider.id });
      this.key += 1;
    },
    save() {
      if (this.selected != "") {
        this.apikey.client_id = parseInt(this.selected.id);
      }
      if (this.currentUser.u.is_admin) {
        this.apikey.service_provider = this.service_provider.id;
      }
      let formData = new FormData();
      formData.append("file", this.file);
      formData.append("data", JSON.stringify(this.apikey));

      this.$store.dispatch(CREATE_APIKEY, formData)
        .then(() => {
          this.$toastr.s(this.is_edit ? "Workstation updated" : "Workstation created");
          this.$emit("closemodal");
        })
        .catch(err => { this.errors = err; });
    },
    async loadDevices() {
      const res = await this.$store.dispatch(GET_API_KEY, this.apikeyid);
      this.devices = res.api_key_devices || [];
    },
    async addDevice() {
      const sn = this.newSerial.trim();
      if (!sn) return;
      await this.$store.dispatch(CREATE_API_KEY_DEVICES, {
        id: 0,
        serial_number: sn,
        api_key: this.apikeyid,
      });
      this.newSerial = "";
      await this.loadDevices();
    },
    startEdit(dev) {
      this.editDevice = { id: dev.id, serial_number: dev.serial_number };
      this.deletingDeviceId = null;
    },
    cancelEdit() {
      this.editDevice = null;
    },
    async saveEdit() {
      if (!this.editDevice.serial_number.trim()) return;
      await this.$store.dispatch(CREATE_API_KEY_DEVICES, this.editDevice);
      this.editDevice = null;
      await this.loadDevices();
    },
    async confirmDeleteDevice(id) {
      await this.$store.dispatch(DELETE_APIKEY_DEVICE, id);
      this.deletingDeviceId = null;
      await this.loadDevices();
    },
  },
  watch: {
    service_provider(newVal) {
      if (newVal == null || newVal == "") {
        this.selected = "";
      }
      if (newVal && newVal.id && !this.is_edit) {
        this.selected = "";
        this.getLocationDataAdmin();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;
$error: #e53e3e;
$green: #276749;

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 27, 62, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
  backdrop-filter: blur(3px);
  padding: 20px;
  font-family: $font;
}

.modal-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(13, 27, 62, 0.22);
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Header ── */
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;
  flex-shrink: 0;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 700;
  color: $dark;
}

.title-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}

.btn-close {
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  border-radius: 6px;
  color: #7a8499;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #fff;
    color: #c62828;
  }
}

/* ── Body ── */
.modal-body {
  padding: 20px 24px 8px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
}

.field-row {
  display: flex;
  gap: 12px;

  .field-group {
    flex: 1;
  }
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #7a8499;
  margin: 0;

  .req {
    color: $error;
    margin-left: 2px;
  }
}

.field-input {
  height: 40px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  color: $dark;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  font-family: $font;
  transition: border-color 0.2s, box-shadow 0.2s;

  &::placeholder {
    color: #b0bac9;
  }

  &:focus {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
  }

  &.has-error {
    border-color: $error;
  }

  &:disabled {
    background: #f7f9fc;
    color: #a0aab8;
    cursor: not-allowed;
  }
}

.field-select {
  height: 40px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  color: $dark;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  font-family: $font;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  transition: border-color 0.2s;

  &:focus {
    border-color: $blue;
  }

  &:disabled {
    background-color: #f7f9fc;
    color: #a0aab8;
    cursor: not-allowed;
  }
}

.field-error {
  font-size: 11px;
  color: $error;
  margin-top: 2px;
}

/* ── Multiselect overrides ── */
.ms-field {
  font-family: $font;
  font-size: 13px;
}

/* ── Toggle ── */
.toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.toggle-label {
  font-size: 13px;
  color: #2d3748;
  font-weight: 500;
}

.toggle-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;

  &:checked+.toggle-slider {
    background: $green;
  }

  &:checked+.toggle-slider::before {
    transform: translateX(16px);
  }

  &:disabled+.toggle-slider {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.toggle-slider {
  width: 36px;
  height: 20px;
  background: #cbd5e0;
  border-radius: 10px;
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    top: 3px;
    left: 3px;
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }
}

/* ── Token display ── */
.token-display {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f0f4fa;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 10px 14px;
}

.token-value {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #2d3748;
  font-weight: 600;
  flex: 1;
  word-break: break-all;
  line-height: 1.5;
}

.btn-copy-token {
  flex-shrink: 0;
  height: 30px;
  padding: 0 12px;
  border: 1.5px solid $blue;
  border-radius: 6px;
  background: #fff;
  color: $blue;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.15s;

  &:hover {
    background: $blue;
    color: #fff;
  }

  .fa-check {
    color: $green;
  }
}

/* ── Section divider ── */
.section-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: $blue;
  border-top: 1px solid #eef0f6;
  padding-top: 14px;
  margin-top: 2px;
}

/* ── File upload ── */
.file-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.file-current {
  font-size: 12px;
  color: #4a5568;
  display: flex;
  align-items: center;
  gap: 5px;
  background: #f7f9fc;
  border: 1px solid $border;
  border-radius: 6px;
  padding: 5px 10px;

  i {
    color: $blue;
  }
}

.btn-file {
  height: 32px;
  padding: 0 14px;
  border: 1.5px solid $blue;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  color: $blue;
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s, color 0.15s;

  input[type="file"] {
    display: none;
  }

  &:hover {
    background: $blue;
    color: #fff;
  }
}

/* ── Livescan Devices ── */
.device-add-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-device-add {
  flex-shrink: 0;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  background: #F58020;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #d96a10;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.device-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.device-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f7f9fc;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 6px 10px;
  min-height: 42px;
}

.device-edit-input {
  flex: 1;
  height: 32px;
}

.device-serial {
  flex: 1;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #2d3748;
  font-weight: 600;
}

.device-confirm-text {
  flex: 1;
  font-size: 12px;
  color: #2d3748;
}

.device-actions {
  display: flex;
  gap: 4px;
}

.btn-dev {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.15s;
  flex-shrink: 0;
  white-space: nowrap;
  padding: 0 8px;
  width: auto;
  height: 28px;
}

.btn-dev-edit {
  background: #eef2fb;
  color: $blue;

  &:hover {
    background: $blue;
    color: #fff;
  }
}

.btn-dev-del {
  background: #fff0f0;
  color: #c62828;

  &:hover {
    background: #c62828;
    color: #fff;
  }
}

.btn-dev-save {
  background: #f0faf4;
  color: $green;

  &:hover {
    background: $green;
    color: #fff;
  }
}

.btn-dev-cancel {
  background: #f5f5f5;
  color: #666;

  &:hover {
    background: #ddd;
  }
}

.device-empty {
  font-size: 12px;
  color: #a0aab8;
  padding: 10px 0;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;

  i {
    font-size: 14px;
  }
}

/* ── Footer ── */
.modal-foot {
  display: flex;
  gap: 10px;
  padding: 16px 24px 24px;
  flex-shrink: 0;
  border-top: 1px solid #f0f3fa;

  button {
    flex: 1;
    height: 40px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: $font;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.15s;
  }

  .btn-cancel {
    background: #fff;
    color: #4a5568;
    border: 1.5px solid $border;

    &:hover {
      border-color: $blue;
      color: $blue;
    }
  }

  .btn-save {
    background: $green;
    color: #fff;
    border: none;

    &:hover {
      background: #1d5230;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
